import { useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Category } from "./CategoryMegaMenu";
import { CategoryMegaMenuPopup } from "./CategoryMegaMenuPopup";
import { getCategoryDisplayPath } from "../utils/categoryUtils";
import { Input } from "../shadcn/ui/Input";

export interface CategoryMegaMenuPopupSelectorProps {
  categories: Category[];
  selectedCategoryId?: string;
  onSelect: (categoryId: string, categoryPath: Category[]) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** When set, column 1 is labeled "Industries" (seller membership industries). */
  industrySelectionMode?: boolean;
}

export const CategoryMegaMenuPopupSelector = ({
  categories,
  selectedCategoryId,
  onSelect,
  placeholder = "Select category",
  label,
  required = false,
  error,
  disabled = false,
  industrySelectionMode = false,
}: CategoryMegaMenuPopupSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Get display path for selected category
  const displayPath = selectedCategoryId
    ? getCategoryDisplayPath(categories, selectedCategoryId)
    : "";

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleConfirm = (categoryId: string, categoryPath: Category[]) => {
    onSelect(categoryId, categoryPath);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect("", []);
  };

  const hasError = !!error;
  const showError = hasError;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium mb-2 text-foreground">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Read-only input field - uses the same Input component as Product Name */}
      <div className="relative">
        <Input
          type="text"
          readOnly
          value={displayPath}
          placeholder={placeholder}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
              e.preventDefault();
              handleOpen();
            }
          }}
          disabled={disabled}
          aria-invalid={hasError}
          className={!disabled ? "cursor-pointer pr-16" : "pr-16"}
        />

        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-2">
          {displayPath && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="pointer-events-auto p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${
              isOpen ? "transform rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Error message */}
      {showError && (
        <p className="mt-1.5 text-sm text-red-600">{error}</p>
      )}

      {/* Validation hint */}
      {!displayPath && !showError && required && (
        <p className="mt-1.5 text-xs text-gray-500">
          Please select at least a subcategory (Level-2 or Level-3)
        </p>
      )}

      {/* Popup */}
      <CategoryMegaMenuPopup
        open={isOpen}
        onOpenChange={setIsOpen}
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        onConfirm={handleConfirm}
        onCancel={handleClose}
        industrySelectionMode={industrySelectionMode}
      />
    </div>
  );
};

